import { useCallback, useMemo, useState } from 'react'
import { useOrg } from '@/admin/hooks/use-org'

type ColumnVisibility = Record<string, boolean>

export interface TableColumnPreferences {
  columnVisibility?: ColumnVisibility
  columnOrder?: string[]
}

interface UseTableColumnPreferencesResult {
  columnVisibility: ColumnVisibility
  setColumnVisibility: (visibility: ColumnVisibility) => void
  columnOrder: string[]
  setColumnOrder: (order: string[]) => void
  saveColumnVisibility: (visibility: ColumnVisibility) => void
  saveColumnOrder: (order: string[]) => void
  applyDefaultColumnPreferences: () => void
}

const KEY_PREFIX = 'raven:table-column-preferences:v1'

/**
 * Fired on window whenever a table's saved column or filter preferences are
 * written, so a surface that snapshots them (a saved dashboard view) can tell
 * the dashboard changed without polling storage.
 */
export const TABLE_PREFERENCES_CHANGED_EVENT = 'raven:table-preferences-changed'
const LEGACY_KEY_PREFIX = 'stream-x:table-column-preferences:v1'

function getStorageKey(tableId: string, tenantSlug: string | null): string | null {
  // Refuse to read or write when the tenant slug is unknown. Falling back
  // to a shared "global" key was the source of cross-tenant column-config
  // bleed when a user had memberships in multiple tenants.
  if (!tenantSlug) return null
  return `${KEY_PREFIX}:${tenantSlug}:${tableId}`
}

function getLegacyStorageKey(
  tableId: string,
  tenantSlug: string | null,
): string | null {
  if (!tenantSlug) return null
  return `${LEGACY_KEY_PREFIX}:${tenantSlug}:${tableId}`
}

function readPreferences(
  storageKey: string | null,
  legacyKey: string | null = null,
): TableColumnPreferences {
  if (typeof window === 'undefined' || !storageKey) return {}

  try {
    let raw = window.localStorage.getItem(storageKey)
    // Migrate from the legacy stream-x: key once per browser, then delete it
    // so the next read goes straight to the new key.
    if (!raw && legacyKey) {
      raw = window.localStorage.getItem(legacyKey)
      if (raw) {
        window.localStorage.setItem(storageKey, raw)
        window.localStorage.removeItem(legacyKey)
      }
    }
    if (!raw) return {}

    const parsed = JSON.parse(raw) as TableColumnPreferences
    return {
      columnVisibility:
        parsed.columnVisibility && typeof parsed.columnVisibility === 'object'
          ? parsed.columnVisibility
          : undefined,
      columnOrder: Array.isArray(parsed.columnOrder) ? parsed.columnOrder : undefined,
    }
  } catch {
    return {}
  }
}

function writePreferences(
  storageKey: string | null,
  preferences: TableColumnPreferences,
) {
  if (typeof window === 'undefined' || !storageKey) return

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(preferences))
    window.dispatchEvent(new Event(TABLE_PREFERENCES_CHANGED_EVENT))
  } catch {
    // Ignore storage failures so table controls continue working in private mode.
  }
}

/**
 * Read / write a table's saved column preferences outside React - used to
 * capture and restore them as part of a saved dashboard view. Same keys and
 * tenant guard as the hook, so a restored view is what the hook reads on mount.
 */
export function readTableColumnPreferences(
  tableId: string,
  tenantSlug: string | null,
): TableColumnPreferences {
  return readPreferences(
    getStorageKey(tableId, tenantSlug),
    getLegacyStorageKey(tableId, tenantSlug),
  )
}

export function writeTableColumnPreferences(
  tableId: string,
  tenantSlug: string | null,
  preferences: TableColumnPreferences,
) {
  writePreferences(getStorageKey(tableId, tenantSlug), preferences)
}

export function useTableColumnPreferences(tableId: string): UseTableColumnPreferencesResult {
  // Source the tenant slug from the live org context rather than a
  // sessionStorage mirror so the localStorage key always agrees with
  // the tenant the page is actually rendering for.
  const { data: org } = useOrg()
  const tenantSlug = org?.organisation?.tenant_slug ?? null

  const storageKey = useMemo(
    () => getStorageKey(tableId, tenantSlug),
    [tableId, tenantSlug],
  )
  const legacyStorageKey = useMemo(
    () => getLegacyStorageKey(tableId, tenantSlug),
    [tableId, tenantSlug],
  )

  const [defaultPreferences, setDefaultPreferences] = useState<TableColumnPreferences>(
    () => readPreferences(storageKey, legacyStorageKey),
  )
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(
    () => defaultPreferences.columnVisibility ?? {},
  )
  const [columnOrder, setColumnOrder] = useState<string[]>(
    () => defaultPreferences.columnOrder ?? [],
  )

  // Reset prefs during render when the storage key changes.
  const [prevStorageKey, setPrevStorageKey] = useState(storageKey)
  if (storageKey !== prevStorageKey) {
    const prefs = readPreferences(storageKey, legacyStorageKey)
    setPrevStorageKey(storageKey)
    setDefaultPreferences(prefs)
    setColumnVisibility(prefs.columnVisibility ?? {})
    setColumnOrder(prefs.columnOrder ?? [])
  }

  const savePreferences = useCallback(
    (updater: (preferences: TableColumnPreferences) => TableColumnPreferences) => {
      setDefaultPreferences((current) => {
        const next = updater(current)
        writePreferences(storageKey, next)
        return next
      })
    },
    [storageKey],
  )

  const saveColumnVisibility = useCallback(
    (visibility: ColumnVisibility) => {
      savePreferences((current) => ({ ...current, columnVisibility: visibility }))
    },
    [savePreferences],
  )

  const saveColumnOrder = useCallback(
    (order: string[]) => {
      savePreferences((current) => ({ ...current, columnOrder: order }))
    },
    [savePreferences],
  )

  const applyDefaultColumnPreferences = useCallback(() => {
    setColumnVisibility(defaultPreferences.columnVisibility ?? {})
    setColumnOrder(defaultPreferences.columnOrder ?? [])
  }, [defaultPreferences])

  return {
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    saveColumnVisibility,
    saveColumnOrder,
    applyDefaultColumnPreferences,
  }
}
